/**
 * hooks/useFormTemplateSave.ts
 *
 * Encapsulates the form template save/draft logic extracted from CreateFormTemplate.
 * Delegates all DB mutations to the save_form_template_transactional RPC, which
 * executes them in a single transaction.
 *
 * Updated Jun 2026 (simplification): all field types (including 'document') are now
 * unified in the 'fields' payload. The separate 'documents' array has been removed.
 * Catalog IDs are no longer stored; the field label is the source of truth.
 * Updated Jun 2026: added short_label to the fields payload for admin display.
 */

import { useState } from 'react';
import { supabase } from '../lib/supabase';
import type { NeedItem } from '../types/needs';
import type { FormTemplateStatus } from '../types/database';
import { captureError } from '../utils/errorReporting';
import { logEvent } from '../utils/analytics';

interface UseFormTemplateSaveParams {
  templateId: string | undefined;
  userId: string;
  isUserAdmin: boolean;
  name: string;
  needs: NeedItem[];
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
  onSaved: () => void;
}

export function useFormTemplateSave({
  templateId,
  userId,
  isUserAdmin,
  name,
  needs,
  showToast,
  onSaved,
}: UseFormTemplateSaveParams) {
  const [saving, setSaving] = useState(false);

  const saveFormTemplate = async (
    status: FormTemplateStatus,
    requireElements: boolean = true
  ): Promise<boolean> => {
    if (!userId) {
      showToast('Utilisateur non authentifié', 'error');
      return false;
    }
    if (!name.trim()) {
      showToast('Le nom du formulaire est obligatoire', 'error');
      return false;
    }
    const nonSystemNeeds = needs.filter((n) => !n.is_system);
    if (requireElements && nonSystemNeeds.length === 0) {
      showToast('Ajoutez au moins un élément en plus du champ email', 'error');
      return false;
    }

    setSaving(true);

    try {
      const positionMap = new Map<string, number>(needs.map((n, i) => [n.id, i]));

      const fields = needs.map((need) => ({
        id: need.id,
        type: need.type,
        label: need.label,
        short_label: need.short_label ?? null,
        description: need.description,
        is_required: need.is_required,
        position: positionMap.get(need.id) ?? 0,
        options:
          need.type === 'single_choice' || need.type === 'multiple_choice'
            ? need.options?.filter((o) => o.trim()) || []
            : null,
        section: need.type === 'document' ? null : (need.section ?? null),
        is_system: need.is_system ?? false,
        conditions: need.conditions || [],
        condition_operator: need.condition_operator || 'or',
      }));

      const conditionUpdates = needs
        .filter((need) => need.conditions && need.conditions.length > 0)
        .map((need) => ({
          id: need.id,
          conditions: need.conditions,
          condition_operator: need.condition_operator || 'or',
        }));

      const payload = {
        template_id: templateId ?? null,
        user_id: userId,
        created_by: isUserAdmin ? userId : null,
        name: name.trim(),
        status,
        is_visible_to_user: !isUserAdmin,
        fields,
        condition_updates: conditionUpdates,
      };

      const { error: rpcError } = await supabase.rpc(
        'save_form_template_transactional',
        { p_payload: payload }
      );
      if (rpcError) throw rpcError;

      if (status === 'active' && !isUserAdmin) {
        logEvent('template_created', { template_id: templateId });
      }

      onSaved();
      return true;
    } catch (error) {
      console.error('Error saving template:', error);
      captureError(error, { hook: 'useFormTemplateSave', action: 'saveFormTemplate', templateId, status });
      showToast('Erreur lors de la sauvegarde', 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { saving, saveFormTemplate };
}
